import { all, call, put, takeLatest, select } from 'redux-saga/effects';

import {
  getTags as getTagsApi,
  createTag as createTagApi,
  updateTag as updateTagApi,
  deleteTag as deleteTagApi,
} from '../../services';
import { displayNotification, checkOnline, getNotification } from '../notifications';
import { REQUEST_TAGS, CREATE_TAG, UPDATE_TAG, DELETE_TAGS, receiveTags } from './actions';
import { getTags } from './selectors';

export function* doRequestTags({ query }) {
  try {
    const { values: tags } = yield call(getTagsApi, query);
    tags.sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
    const oldTags = yield select(getTags);
    if (JSON.stringify(oldTags) !== JSON.stringify(tags)) {
      yield put(receiveTags(tags));
    }
  } catch (error) {
    yield console.error('Unable to fetch tags:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getTags', 'error')()));
  }
}

export function* doCreateTag({ tag, callback }) {
  try {
    const tags = yield select(getTags);
    let payload = { ...tag };
    let data;
    const existingTag = tags.find((t) => t.name === tag.name);

    // If the tag exists in another category, update it to add the new category
    if (existingTag) {
      payload = {
        ...existingTag,
        categories: [...new Set([...(existingTag.categories || []), ...(tag.categories || [])])],
      };
      data = yield updateTagApi(existingTag.id, payload);
    } else {
      data = yield createTagApi(payload);
    }
    if (callback) {
      yield call(doRequestTags, { query: { site: tag.site } });
      yield call(callback, data.id);
      yield put(displayNotification(getNotification('createTag', 'success')(tag.name)));
    }
  } catch (error) {
    console.error('Unable to create tag:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('createTag', 'error')()));
  }
}

export function* doUpdateTag({ tag }) {
  const { id, ...tagUpdate } = tag;
  try {
    yield call(updateTagApi, id, tagUpdate);
    yield call(doRequestTags, { query: { site: tag.site } });
    yield put(displayNotification(getNotification('updateTag', 'success')(id)));
  } catch (error) {
    console.error('Unable to update tag:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('updateTag', 'error')(id)));
  }
}

function* doDeleteTag(tag, category) {
  try {
    if (tag.categories?.includes(category) && tag.categories.length > 1) {
      const payload = { ...tag };
      payload.categories = payload.categories.toSpliced(payload.categories.indexOf(category), 1);
      yield call(updateTagApi, tag.id, payload);
    }
    if (tag.categories?.includes(category) && tag.categories.length === 1) {
      yield call(deleteTagApi, tag.id);
    }
    yield put(displayNotification(getNotification('deleteTag', 'success')(tag.id)));
  } catch (error) {
    console.error('Unable to delete tag:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('deleteTag', 'error')(tag.id)));
  }
}

export function* doDeleteTags({ siteId, tagIds, category }) {
  const tags = yield select(getTags);
  yield all(
    tagIds.map((tagId) => {
      const tag = tags.find((t) => t.id === tagId);
      if (tag) {
        return call(doDeleteTag, tag, category);
      }
      return null;
    })
  );
  yield call(doRequestTags, { query: { site: siteId } });
}

export const sagas = [
  takeLatest(REQUEST_TAGS, doRequestTags),
  takeLatest(CREATE_TAG, doCreateTag),
  takeLatest(UPDATE_TAG, doUpdateTag),
  takeLatest(DELETE_TAGS, doDeleteTags),
];
